import { MAX_FILE_SIZE } from 'src/types/UserDocuments';

/**
 * Default cap on files per upload batch — FileUploadDropzone's historical
 * default, shared so other entry points (the always-on collection-thread
 * dropzone) enforce the same limit.
 */
export const DEFAULT_MAX_UPLOAD_FILES = 10;

export interface FileValidationError {
  file: File;
  error: string;
}

export interface FileValidationResult {
  /** Files that passed validation, in their original order. */
  validFiles: File[];
  /** One entry per rejected file (or a single entry for a rejected batch). */
  errors: FileValidationError[];
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * The upload constraints FileUploadDropzone enforces, extracted as a pure
 * function so every upload entry point applies identical rules. A batch larger
 * than `maxFiles` is rejected whole (one error, nothing uploads); otherwise
 * oversized files are rejected individually and the rest proceed.
 */
export function validateFilesForUpload(
  files: File[],
  maxFiles: number = DEFAULT_MAX_UPLOAD_FILES,
): FileValidationResult {
  if (files.length > maxFiles) {
    return {
      validFiles: [],
      errors: [{ file: files[0], error: `Maximum ${maxFiles} files allowed` }],
    };
  }

  const errors: FileValidationError[] = [];
  const validFiles: File[] = [];
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      errors.push({
        file,
        error: `File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`,
      });
    } else {
      validFiles.push(file);
    }
  }
  return { validFiles, errors };
}
